import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import SmartScrollRow from "./SmartScrollRow.js";
import { setCurrentCategoryDispatch } from "../../server/Category";
import "./SubNavbar.scss";

/**
 * Re-computes --shell-fixed-stack-bottom by reading the current bottom edge
 * of every nav.sub-navbar element in the page plus the header wrapper.
 * Called whenever any sub-navbar mounts, resizes, or unmounts so the <main>
 * padding-top stays accurate even when bars appear/disappear asynchronously.
 */
function syncFixedStackBottom() {
  const root = document.documentElement;
  const headerWrap = document.querySelector(".header-wrapper");
  const subnavs = Array.from(document.querySelectorAll("nav.sub-navbar"));
  const headerBottom = headerWrap
    ? headerWrap.getBoundingClientRect().bottom
    : 0;
  const subBottom = subnavs.length
    ? Math.max(...subnavs.map((n) => n.getBoundingClientRect().bottom))
    : 0;
  const fixed = Math.max(0, Math.ceil(Math.max(headerBottom, subBottom)));
  root.style.setProperty("--shell-fixed-stack-bottom", `${fixed}px`);
}

const SubNavbar = ({
  fatherSlug,
  currentSubCategory,
  currentCategories,
  level = 1,
  setPendingScrollSlug,
}) => {
  const { pathname } = useLocation();
  const [children, setChildren] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);
  const dispatch = useDispatch();
  const navRef = useRef(null);

  const { includeInactive, role } = useSelector((state) => ({
    includeInactive: state.category.includeInactive,
    role: state.user.role,
  }));

  useEffect(() => {
    setChildren(null);
    setCurrentCategory(null);
  }, [pathname]);

  /* Keep --shell-fixed-stack-bottom in sync whenever this bar mounts/resizes/unmounts */
  useEffect(() => {
    const el = navRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    syncFixedStackBottom();

    const ro = new ResizeObserver(() =>
      requestAnimationFrame(syncFixedStackBottom),
    );
    ro.observe(el);

    return () => {
      ro.disconnect();
      /* After unmount, give React one tick to remove the element, then re-measure */
      setTimeout(syncFixedStackBottom, 0);
    };
  }, []);

  const handleCategoryClick = (slug) => {
    setPendingScrollSlug(slug);
  };

  const updateCurr = useCallback(
    (slug) => {
      const curr = currentCategories.children.find((cat) => cat.slug === slug);
      if (curr) {
        if (curr.children.filter((cat) => !cat.isFamily).length !== 0) {
          dispatch(setCurrentCategoryDispatch(curr));
          setChildren(curr);
          setCurrentCategory(curr.slug);
        } else {
          dispatch(setCurrentCategoryDispatch(currentCategories));
          setCurrentCategory(null);
          setChildren(null);
        }
      } else {
        for (let cat of currentCategories.children) {
          for (let subCat of cat.children) {
            if (subCat.slug === slug) {
              dispatch(setCurrentCategoryDispatch(cat));
              setChildren(cat);
              setCurrentCategory(cat.slug);
              return;
            }
          }
        }
      }
    },
    [currentCategories, dispatch],
  );

  useEffect(() => {
    if (currentSubCategory) {
      updateCurr(currentSubCategory);
    }
  }, [currentSubCategory, updateCurr]);

  return (
    <>
      <nav
        ref={navRef}
        className={`sub-navbar sub-navbar--level-${level}${role ? " sub-navbar--admin" : ""}`}
        aria-label={`תת־קטגוריות רמה ${level}`}
      >
        <SmartScrollRow
          rtl
          shrinkToContent
          trackClassName="sub-navbar__scroll-track"
        >
          {currentCategories.children.map(
            (category) =>
              !category.isFamily &&
              category.status === "ACTIVE" &&
              category.totalProducts > 0 &&
              (!includeInactive || !role
                ? category.activeProducts > 0
                : true) && (
                <a
                  key={category.slug}
                  href="/"
                  className={`sub-navbar-item ${category.slug === currentCategory || category.slug === currentSubCategory ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleCategoryClick(category.slug);
                  }}
                >
                  {category.name}
                </a>
              ),
          )}
        </SmartScrollRow>
      </nav>
      {children &&
        children.children.filter((cat) => !cat.isFamily).length !== 0 && (
          <SubNavbar
            fatherSlug={currentCategory}
            currentSubCategory={currentSubCategory}
            currentCategories={children}
            level={level + 1}
            setPendingScrollSlug={setPendingScrollSlug}
          />
        )}
    </>
  );
};

export default SubNavbar;
